import { Box, Button, Grid, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import { addEditSystemSchema } from "../../schemas";
import useFetchSystemById from "../../hooks/useFetchSystemById";
import { addSystem, updateSystem } from "../../services/systemService";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import { ISystem } from "../../types/System";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useToast } from "../../services/utils";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

const AddEditSystem: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: systemId } = useParams();
  const isEditMode = Boolean(systemId);
  const [formData, setFormData] = useState<ISystem>({
    value: "",
  });
  const { isAdmin } = useAuth();
  const { t } = useAppTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  // hooks
  const [systemData] = useFetchSystemById({
    setIsLoading,
    currentSystemId: systemId,
  });

  // Set form data in state
  const handleFormData = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Validate individual field
  const validateField = async (name: string, value: any) => {
    try {
      await addEditSystemSchema.validateAt(name, { [name]: value });
      setErrors((prevErrors: any) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  // Handle save system
  const onSaveSystem = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await addEditSystemSchema.validate(formData, {
        abortEarly: false,
      });
      const reqBody = {
        ...formData,
      };
      let response: any;
      if (systemId) {
        // Update existing system
        response = await updateSystem({
          currentSystemId: systemId,
          payload: reqBody,
        });
      } else {
        // Create new system
        response = await addSystem({ payload: reqBody });
      }
      if (response) {
        showToast(
          systemId ? t("systems.toast.updated") : t("systems.toast.created"),
          "success"
        );
        navigate("/system");
      }
    } catch (validationErrors: any) {
      if (validationErrors.inner) {
        // Yup validation errors
        const newErrors: { [key: string]: string } = {};
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          }
        );
        setErrors((prevErrors) => ({
          ...prevErrors,
          ...newErrors,
        }));
      } else {
        console.error(
          `Cannot ${systemId ? "update" : "create"} system:`,
          validationErrors
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (systemData && systemData?.id) {
      // Edit mode: populate with system data
      setFormData({
        ...systemData,
      });
      setInitialFormData(JSON.parse(JSON.stringify(systemData)));
    } else if (!systemId) {
      // Add mode: set initial form data to match the default formData
      setInitialFormData({ value: "" });
    }
  }, [systemData, systemId]);

  useEffect(() => {
    if (systemId && systemData?.value) {
      setCurrentBreadcrumbPage({
        name: `Edit ${systemData.value}`,
        id: systemData.id,
      });
    } else {
      setCurrentBreadcrumbPage({
        name: systemId
          ? t("common.buttons.edit") + " System"
          : t("common.buttons.add") + " System",
        id: systemId || null,
      });
    }
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [systemId, systemData, setCurrentBreadcrumbPage, t]);

  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(formData).length > 0) {
      const hasFormChanges =
        JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [formData, initialFormData]);

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode ? `/system/${systemId}` : "/system",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: systemId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast(t("systems.toast.payloadValid"), "success");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || t("systems.toast.validationFailed"),
        "error"
      );
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify({ ...formData }, null, 2)
    );
    showToast(t("systems.toast.payloadCopied"), "success");
  };

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={onSaveSystem} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                {t("systems.form.systemName")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="value"
                onChange={handleFormData}
                className={`form-control ${errors?.value ? "has-error" : ""}`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.value}
                helperText={errors?.value || ""}
                value={formData?.value}
                placeholder={t("systems.form.placeholder")}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            {/* <Button
              type="button"
              color="secondary"
              variant="contained"
              onClick={() => navigate("/system")}
              className="btn-orange btn-dark"
            >
              Cancel
            </Button> */}
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isEditMode ? !hasChanges : false}
            >
              <SaveOutlinedIcon /> &nbsp;{" "}
              {isEditMode ? t("systems.form.update") : t("systems.form.save")}
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle={t("systems.form.jsonDialogTitle")}
        payload={{ ...formData }}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditSystem;
